/**
 * Полоса пакета сбора под шапкой подборки: сколько поисков (слово × язык)
 * уже сделано и что идёт сейчас.
 *
 * Прогресс считаем по счётчикам пакета (`batch`), а не по списку задач темы:
 * там только 20 новых, и дочерние пакета его заполнят. Завершённый пакет —
 * неяркая строка итога: это справка, а не событие.
 */
import { Loader2, Square } from "lucide-react";

import { btn } from "@/components/trends/ui";
import { isBatchActive, type BatchSummary } from "@/lib/trends";

export function BatchBar({
  batch,
  onCancel,
  cancelling,
}: {
  batch: BatchSummary;
  /** Нет — смотрят чужую подборку: остановить нельзя. */
  onCancel?: (() => void) | undefined;
  cancelling: boolean;
}) {
  const finished = batch.done + batch.failed + batch.cancelled;
  const active = isBatchActive(batch);
  // И в фазе перевода: пакет, ещё не разложенный на поиски, снимается целиком (иначе он заплатит за перевод).
  const stop = onCancel ? (
    <button
      type="button"
      className={`${btn} ml-auto`}
      onClick={onCancel}
      disabled={cancelling}
      title="Снять из очереди всё, что ещё не началось; текущий поиск доработает"
    >
      <Square size={12} />
      Остановить
    </button>
  ) : null;

  if (batch.status === "failed" && !active)
    return (
      <div className="whitespace-pre-wrap break-words rounded-[var(--radius-ui)] bg-err-bg px-3 py-2 text-[12.5px] text-err">
        Пакет сбора не удался: {batch.error ?? "причина не записана"}
      </div>
    );

  // Пакет без поисков: план пуст (всё собрано) или его остановили до раскладки.
  if (!active && batch.total === 0)
    return (
      <div className="px-1 text-[12px] text-fg-faint">
        {batch.status === "cancelled" ? "Пакет остановлен до начала сбора" : "Нечего собирать — всё уже собрано"}
      </div>
    );

  if (!active)
    return (
      <div className="px-1 text-[12px] text-fg-faint">
        Последний пакет: готово {batch.done}, ошибок {batch.failed}, остановлено {batch.cancelled}
      </div>
    );

  // Пакет принят, но дочерних задач ещё нет: воркер переводит фразы и строит план.
  if (batch.total === 0)
    return (
      <div className="flex items-center gap-2 rounded-[var(--radius-ui)] bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
        <Loader2 size={13} className="animate-spin" />
        Перевожу фразы и готовлю план…
        {stop}
      </div>
    );

  const pct = Math.min(100, Math.round((finished / batch.total) * 100));
  return (
    <div className="grid gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <Loader2 size={13} className="animate-spin text-warn" />
        <span className="tnum text-fg">
          {finished} / {batch.total}
        </span>
        {batch.running ? (
          <span className="text-fg-muted">
            · сейчас: {batch.running.name ?? `тип #${batch.running.query_id}`} ({batch.running.lang})
          </span>
        ) : null}
        <span className={batch.failed ? "text-err" : "text-fg-muted"}>· ошибок {batch.failed}</span>
        {stop}
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-neutral-bg"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
